// UGS-kontrakt — UBEVOKTEDE BAKKESENSORER (Unattended Ground Sensors) for ULL,
// jegertroppens web-app. Søster av `hydrofon.ts` (NJORD/kystpost), men den
// operative situasjonen er en helt annen: NJORD sitter FAST og ser UTOVER, ULL
// LEGGER UT sensorer langs ruter og flaskehalser og observerer derfra.
//
// DEN ENE INNSIKTEN HELE FILA ER BYGD RUNDT:
//
//   En UGS-utløsning er et PUNKT i rom og tid — «noe passerte HER, klokka DA».
//
// Det er STERKERE posisjonsinformasjon enn alt NJORD har passivt: en hydrofon-
// eller RF-peiling gir en LINJE (retning uten avstand), mens en utløsning gir en
// DISK med radius = deteksjonsradius. Derfor er `ugsUtlosningTilTrack()` det
// motsatte av `hydrofonDeteksjonTilTrack()`: her settes `cepM`, og posisjonen er
// IKKE merket estimert. Det er lov, fordi det er sant.
//
// Men baksiden er like viktig: EN ENKELT UTLØSNING BÆRER INGEN RETNING. En
// geofon vet ikke hvor skrittet gikk, bare at bakken ristet. Retning og fart
// kommer fra REKKEFØLGEN av utløsninger på FLERE sensorer, og det er nettopp
// derfor `sekvensSpor()` er hovedfunksjonen i denne fila og ikke et tillegg.
// Unntaket er sensorer som FYSISK er retningsbestemte (dobbelstråle-IR): de får
// en egen union-variant med retning. Alle andre varianter MANGLER retningsfeltet
// strukturelt — samme grep som `HydrofonDeteksjonEnkelt`, og av samme grunn: et
// valgfritt `retningDeg?` blir lest som 0 (nord) av første klient som glemmer
// `undefined`.
//
// TRE TING SOM ER MODELLERT BEVISST STRENGT:
//
//  1) TIDSUSIKKERHET ER PÅKREVD, IKKE VALGFRI. En billig UGS-node har en RTC som
//     drifter sekunder per døgn. All sekvensanalyse er en divisjon på
//     tidsdifferansen, så klokkeslark er den DOMINERENDE feilkilden i hele
//     appen — verre enn deteksjonsradien. Et felt som fikk stå tomt ville blitt
//     lest som «null slark», og da får operatøren et fartstall med to desimaler
//     som er ren fantasi. Derfor `tidsusikkerhetMs` med `.positive()` i
//     fellesfeltene: 0 er også en løgn.
//
//  2) HVORDAN POSISJONEN BLE BESTEMT MÅ FØLGE SENSOREN. En sensor plassert etter
//     kart og kompass i mørket kan stå 50 m fra der operatøren tror. Den feilen
//     går rett inn i BÅDE fartsberegningen og dekningsanalysen. `posisjonMetode`
//     er derfor påkrevd, og `POSISJONSFEIL_M_UVERIFISERT` gjør konsekvensen
//     regnbar i stedet for anekdotisk.
//
//  3) FART ER ET INTERVALL, ALDRI ETT TALL. To sensorer 80 m fra hverandre, ±20 m
//     deteksjonsradius hver og ±2 s samlet klokkeslark, gir «1,4–7,2 m/s» — altså
//     alt fra rolig gange til spurt. Å vise «4,0 m/s» der ville vært den samme
//     typen løgn som en peiling uten usikkerhet. `sekvensSpor()` returnerer derfor
//     `fartMinMs`/`fartMaksMs` og en `begrunnelse` operatøren kan etterprøve.
//
// SIKKERHET: ingen URL-er, vertsnavn eller credentials i kontrakten. Bilder
// refereres med `bildeId` — en opak id som mediatjenesten slår opp bak RBAC.
// `bildeId` valideres aktivt mot URL-lignende innhold, se `BildeReferanse`.
//
// UVERIFISERT-merking: alt som er antatt fra litteratur/erfaring og ikke målt i
// eget terreng er merket UVERIFISERT. Terskelverdiene i `ugsKlassifiser()` er de
// viktigste — se den lange kommentaren der om hvorfor seismisk klassifisering av
// «person» er svak, og hvorfor en elg ødelegger den.
import { z } from 'zod';
import { project, haversineM, ringPolygon, featureCollection } from './geo.js';
import { HoydeReferanse } from './track.js';
// ── Grenser (DoS-vern, jf. hydrofon.ts MAX_POSTER) ──────────────────────────
/** Maks sensorer i én analyse (et jegerlag legger ut titalls, ikke tusener). */
export const MAX_SENSORER = 512;
/** Maks utløsninger i én sekvensanalyse. */
export const MAX_UTLOSNINGER = 256;
/** Maks knekkpunkt i en rute til dekningsanalysen. */
export const MAX_RUTEPUNKT = 512;
/** Maks celler i dekningsanalysen. Taket gjør at en 100 km rute med 0,5 m steg
 *  ikke låser klienten — oppløsningen degraderes og RAPPORTERES i stedet. */
export const MAX_CELLER = 20_000;
// ── Sensortyper ─────────────────────────────────────────────────────────────
/**
 * Sensorart. Rekkefølgen er den operative: seismisk og PIR er arbeidshestene,
 * kamera er det eneste som gir noe et menneske kan verifisere med egne øyne.
 *
 *  `seismisk`    — geofon/akselerometer i bakken. Rundstrålende av fysikk.
 *  `pir`         — passiv IR. Enkeltstråle gir «noe krysset»; dobbelstråle gir
 *                  RETNING (se `UgsUtlosningRetning`).
 *  `magnetisk`   — magnetometer. Reagerer på jern i bevegelse (våpen, kjøretøy),
 *                  ikke på en person i syntetiske klær. Rundstrålende.
 *  `akustisk`    — mikrofon i luft. ÉN mikrofon peiler ikke (samme fysikk som
 *                  enkelthydrofonen i hydrofon.ts) — derfor rundstrålende her.
 *  `kamera`      — viltkamera-stil: bilde ved utløsning, ikke video.
 *  `brytertråd`  — snor/tråd som brytes. Geometrisk en LINJE, ikke en disk.
 */
export const UgsArt = z.enum(['seismisk', 'pir', 'magnetisk', 'akustisk', 'kamera', 'brytertråd']);
/**
 * Hvordan sensorens posisjon ble bestemt. Dette er IKKE metadata-pynt: metoden
 * er en multiplikator på hver eneste tallverdi som kommer ut av fila.
 *
 *  `gps`      — lest av GPS på stedet. Noen få meter.
 *  `kartlagt` — pekt ut i kart etter terrengholdepunkt. Titalls meter i skog.
 *  `anslått`  — «omtrent der stien svinger». En sekvensberegning på anslåtte
 *               posisjoner kan ta feil av fartsregimet fullstendig, og MÅ derfor
 *               kunne skilles i UI-et. `sekvensSpor()` og `dekningshull()`
 *               advarer eksplisitt når en anslått sensor er med.
 */
export const PosisjonsMetode = z.enum(['gps', 'kartlagt', 'anslått']);
/**
 * 1σ posisjonsfeil per metode, meter. ALLE TRE ER UVERIFISERT: de er
 * erfaringstall for håndholdt GNSS under trekrone (gps), kartutpeking i
 * barskog (kartlagt) og hukommelse i mørket (anslått). Skal måles med egen
 * prosedyre og eget utstyr før de brukes til noe som utløser handling.
 * `UgsSensor.posisjonUsikkerhetM` overstyrer tabellen når den faktisk er målt.
 */
export const POSISJONSFEIL_M_UVERIFISERT = {
    gps: 5,
    kartlagt: 15,
    anslått: 50,
};
/**
 * Nominell deteksjonsrekkevidde per art, meter. UVERIFISERT og bevisst LAV:
 * databladtall måles i fri sikt på hardt underlag. Myr demper seismikk kraftig,
 * tett gran stopper PIR helt, og løvverk halverer akustisk rekkevidde.
 * Tabellen er et STARTPUNKT for utlegging, ikke en sannhet — derfor brukes den
 * ikke av noen beregning i denne fila. Radien skal måles i det terrenget
 * sensoren faktisk ligger i, og settes eksplisitt på sensoren.
 */
export const TYPISK_DEKNING_M_UVERIFISERT = {
    seismisk: 25,
    pir: 15,
    magnetisk: 5,
    akustisk: 60,
    kamera: 20,
    brytertråd: 8,
};
// ── Deteksjonsgeometri ──────────────────────────────────────────────────────
/**
 * Sensorens dekning som geometri. DISKRIMINERT UNION fordi formen bestemmer
 * hva sensoren i det hele tatt KAN si:
 *
 *  - `rundstrålende`: bare radius. En geofon som mangler retningsfelt kan ikke
 *    ved et uhell bli lest som retningsbestemt.
 *  - `sektor`: radius + senterretning + åpningsvinkel. Et kamera med 50° FOV
 *    dekker 1/7 av disken sin, og å tegne hele disken ville gjort
 *    `dekningshull()` ubrukelig (den ville sagt «dekket» om et sted kameraet
 *    aldri ser).
 *  - `linje`: en brytertråd er en STREK mellom to punkt, med ~ingen utstrekning
 *    på tvers. Modellert som en disk ville en 8 m tråd «dekket» 200 m² den ikke
 *    dekker. Dette er nettopp den løgnen dekningsanalysen finnes for å unngå.
 */
export const UgsGeometriRund = z.object({
    form: z.literal('rundstrålende'),
    /** Deteksjonsradius i meter — den avstanden sensoren FAKTISK utløser på i
     *  DETTE terrenget, ikke databladets frisiktstall. */
    radiusM: z.number().positive().max(5000),
});
export const UgsGeometriSektor = z.object({
    form: z.literal('sektor'),
    radiusM: z.number().positive().max(5000),
    /** Sektorens senterretning, sann peiling (grader fra nord). PÅKREVD: en
     *  sektor uten retning er en disk, og da skal `rundstrålende` brukes. */
    retningDeg: z.number().min(0).max(360),
    /** Full åpningsvinkel (grader). 360 = rundstrålende sektor (360°-kamera) og
     *  behandles som disk av `dekkerPunktUgs()`. */
    apningsvinkelDeg: z.number().positive().max(360),
    /** 1σ på senterretningen — for et kamera pekt inn etter kompass i mørket er
     *  dette lett 10°, og det flytter sektorkanten titalls meter på 100 m. */
    retningUsikkerhetDeg: z.number().min(0).max(90).default(0),
});
export const UgsGeometriLinje = z.object({
    form: z.literal('linje'),
    /** Trådens totale lengde (m). Sensorposisjonen er trådens MIDTPUNKT. */
    lengdeM: z.number().positive().max(2000),
    /** Trådens akse, grader fra nord (0–180 holder geometrisk, men vi tillater
     *  0–360 så operatøren kan skrive den retningen han faktisk strakk den). */
    retningDeg: z.number().min(0).max(360),
});
export const UgsGeometri = z.discriminatedUnion('form', [
    UgsGeometriRund,
    UgsGeometriSektor,
    UgsGeometriLinje,
]);
// ── Batteri og samband ──────────────────────────────────────────────────────
/** Er verdien MÅLT av noden, eller ANSLÅTT (av operatør/av liggetid)? */
export const MaltEllerAnslatt = z.enum(['målt', 'anslått']);
/**
 * Batteritilstand. DISKRIMINERT UNION på enhet, ikke ett felt som «kanskje er
 * prosent»: en billig node rapporterer terminalspenning, og «prosent» fra samme
 * node er nesten alltid en lineær omregning av en utladningskurve som er FLAT
 * gjennom 80 % av levetiden. Da er 60 % og 20 % samme spenning ±0,05 V.
 * Spenningen er i det minste en måling. Blandes de i ett felt, tror operatøren
 * han har fire dager igjen når han har fire timer.
 */
export const UgsBatteriVolt = z.object({
    enhet: z.literal('volt'),
    /** Terminalspenning i volt. */
    spenningV: z.number().min(0).max(60),
    /** Antall celler, om kjent — 3,4 V betyr helt ulike ting for 1S og 2S. */
    celler: z.number().int().min(1).max(24).optional(),
    kilde: MaltEllerAnslatt,
});
export const UgsBatteriProsent = z.object({
    enhet: z.literal('prosent'),
    prosent: z.number().min(0).max(100),
    kilde: MaltEllerAnslatt,
});
export const UgsBatteri = z.discriminatedUnion('enhet', [UgsBatteriVolt, UgsBatteriProsent]);
/**
 * Sambandslenkens kvalitet. `ingen-lenke` er ikke en feil: en ren loggende
 * sensor som hentes fysisk er et helt gyldig valg for et jegerlag (den stråler
 * ikke, altså kan den ikke peiles inn). Men den gir ingen VARSEL, og det skiller
 * den operativt fra `tapt` — der vi forventet varsel og ikke fikk det.
 */
export const LenkeKvalitet = z.enum(['god', 'marginal', 'tapt', 'ingen-lenke']);
export const UgsLenke = z.object({
    kvalitet: LenkeKvalitet,
    /** Sist gang noden ble hørt (ms epoch). Skiller «stille fordi ingen har gått
     *  der» fra «stille fordi den er død» — jf. SENSORGRAF §2.1 om alder per
     *  nyttelast. */
    sistHortMs: z.number().int().nonnegative().optional(),
    rssiDbm: z.number().min(-140).max(0).optional(),
    snrDb: z.number().optional(),
    /** Antall hopp i meshen fram til noden. Flere hopp = større forsinkelse på
     *  varselet, og forsinkelsen er det som avgjør om det er handlingsbart. */
    hopp: z.number().int().min(0).max(32).optional(),
});
/**
 * Hvor godt sensoren er skjult. For et jegerlag er dette ikke kosmetikk:
 * en OPPDAGET sensor er verre enn ingen sensor. Den forteller motparten hvor
 * laget tror hovedaksen går, at området overvåkes, og den kan brukes til
 * villeding (utløs den bevisst, gå et annet sted). Derfor er skjultGrad et
 * PÅKREVD felt — den som legger ut skal ta stilling til det på stedet.
 */
export const SkjultGrad = z.enum(['godt-skjult', 'delvis-skjult', 'synlig']);
export const Eksponeringsrisiko = z.enum(['lav', 'middels', 'høy']);
// ── Sensoren ────────────────────────────────────────────────────────────────
export const UgsSensorSkjema = z
    .object({
    sensorId: z.string().min(1).max(64),
    navn: z.string().min(1).max(80),
    art: UgsArt,
    /**
     * Laget sensoren tilhører. PÅKREVD fordi SENSORGRAF §2.1 er bindende:
     * echelon = LAG, og aggregeringen i kartet skjer på organisasjon, aldri på
     * geometri. Sensoren er en nyttelast med EGEN posisjon, knyttet til laget
     * med en bindestrek i kartet.
     */
    lagId: z.string().min(1).max(64),
    /** Sensorens EGEN posisjon (ikke lagets). Jf. SENSORGRAF §2.1 b). */
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    /** Høyde over `hoydeReferanse`. Valgfri: for en bakkesensor er høyden
     *  sjelden interessant i seg selv, men den er det for siktlinjer til
     *  kamera og for varme/kalde luftlag i akustikken. Samme regel som
     *  `Track.hoydeM`: referansen er påkrevd så snart tallet er satt. */
    hoydeM: z.number().min(-500).max(9000).optional(),
    hoydeReferanse: HoydeReferanse.optional(),
    posisjonMetode: PosisjonsMetode,
    /** Målt 1σ posisjonsfeil (m). Overstyrer `POSISJONSFEIL_M_UVERIFISERT`. */
    posisjonUsikkerhetM: z.number().min(0).max(5000).optional(),
    /** Når sensoren ble lagt ut (ms epoch). Alderen forteller om batteritallet
     *  fortsatt er troverdig, og om noen kan ha funnet den. */
    utlagtMs: z.number().int().nonnegative(),
    /** Hvem som la den ut — navn/kallesignal/id. Sporbarhet: den som la den ut
     *  er den som vet hvordan den står, og den som skal hente den. */
    utlagtAv: z.string().min(1).max(64),
    geometri: UgsGeometri,
    batteri: UgsBatteri,
    lenke: UgsLenke,
    skjultGrad: SkjultGrad,
    /** Operatørens egen vurdering. `antattEksponeringsrisiko()` gir et forslag
     *  når feltet står tomt — men operatørens vurdering på stedet vinner. */
    eksponeringsrisiko: Eksponeringsrisiko.optional(),
    /** Er sensoren i drift NÅ? En hentet eller utladet sensor dekker ingenting,
     *  og `dekningshull()` MÅ utelate den — ellers regner analysen med dekning
     *  som ikke finnes, og det er den ene feilen analysen finnes for å hindre. */
    aktiv: z.boolean().default(true),
    /** Når sensoren ble hentet inn (ms epoch). Utelukker `aktiv: true`. */
    fjernetMs: z.number().int().nonnegative().optional(),
    /**
     * Nodens tidskilde. Går IKKE inn i noen beregning (usikkerheten oppgis per
     * utløsning), men er den første tingen å se på når en sekvens er umulig.
     */
    klokkeKilde: z.enum(['gps', 'nettverkssynk', 'intern-rtc', 'ukjent']).default('ukjent'),
    merknad: z.string().max(400).optional(),
})
    .superRefine((s, ctx) => {
    // Høyde uten referanse er et tall uten mening (jf. track.ts). Motsatt vei
    // er en referanse uten tall bare støy, og skjuler at høyden mangler.
    if (s.hoydeM !== undefined && s.hoydeReferanse === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['hoydeReferanse'],
            message: 'hoydeM krever hoydeReferanse (msl/agl/trykk/hae) — et høydetall uten referanse kan ikke tegnes i 3D',
        });
    }
    if (s.hoydeReferanse !== undefined && s.hoydeM === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['hoydeM'],
            message: 'hoydeReferanse uten hoydeM er meningsløs',
        });
    }
    // Art og geometri MÅ stemme fysisk. Dette er samme håndhevelse som
    // hydrofon.ts gjør på type/array: en modell som tillater «seismisk sektor»
    // ville før eller senere fått nedstrøms kode til å tro at en geofon vet
    // hvilken vei skrittet gikk.
    const rundKrav = ['seismisk', 'magnetisk', 'akustisk'];
    if (rundKrav.includes(s.art) && s.geometri.form !== 'rundstrålende') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['geometri', 'form'],
            message: `art «${s.art}» er fysisk rundstrålende (bakkevibrasjon/magnetfelt/lyd fra ett punkt har ingen retning) — bruk form «rundstrålende»`,
        });
    }
    if (s.art === 'brytertråd' && s.geometri.form !== 'linje') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['geometri', 'form'],
            message: 'brytertråd er geometrisk en linje, ikke en disk — bruk form «linje» med lengdeM og retningDeg',
        });
    }
    if (s.geometri.form === 'linje' && s.art !== 'brytertråd') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['geometri', 'form'],
            message: `form «linje» er forbeholdt brytertråd (art er «${s.art}»)`,
        });
    }
    if (s.art === 'kamera' && s.geometri.form !== 'sektor') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['geometri', 'form'],
            message: 'kamera har et synsfelt — bruk form «sektor» (et 360°-kamera settes som sektor med apningsvinkelDeg 360)',
        });
    }
    // En hentet sensor kan ikke være i drift. To felt som motsier hverandre er
    // verre enn ett tomt felt, fordi begge ser autoritative ut i UI.
    if (s.fjernetMs !== undefined && s.aktiv) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['aktiv'],
            message: 'fjernetMs er satt, men aktiv er true — en hentet sensor dekker ingenting',
        });
    }
    if (s.fjernetMs !== undefined && s.fjernetMs < s.utlagtMs) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['fjernetMs'],
            message: 'fjernetMs er før utlagtMs',
        });
    }
});
// ── Utløsning ───────────────────────────────────────────────────────────────
/**
 * Bilde-/mediereferanse. En OPAK id, ikke en URL.
 *
 * HVORFOR DETTE VALIDERES OG IKKE BARE DOKUMENTERES: den enkleste måten å lekke
 * et internt vertsnavn (eller en signert URL med token) ut i en kontrakt som
 * krysser E2EE-grensen, er å skrive den i et felt som heter «bilde». Regexen
 * tillater bare id-tegn, og `superRefine` avviser eksplisitt `://`, `@` og `/`
 * med en melding som forklarer hvorfor — så den som prøver skjønner hva de skal
 * gjøre i stedet (slå opp id-en i mediatjenesten, bak RBAC).
 */
const BILDE_ID_MONSTER = /^[A-Za-z0-9._:-]{1,128}$/;
export const BildeReferanse = z
    .string()
    .min(1)
    .max(128)
    .superRefine((v, ctx) => {
    if (!BILDE_ID_MONSTER.test(v)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'bildeId skal være en OPAK id (A–Z, 0–9, . _ : -) — ikke URL, vertsnavn, filsti eller token. Media slås opp via id bak RBAC',
        });
    }
});
/** Hvorfor kameraet tok bildet. Avgjør hvor mye et TOMT bilde betyr: et
 *  tidsintervall-bilde uten folk på er ingen informasjon, mens et PIR-utløst
 *  bilde uten folk på betyr at NOE varmt beveget seg og var ute av bildet. */
export const BildeArsak = z.enum(['pir', 'tidsintervall', 'manuell', 'bevegelse-i-bilde']);
/** Målklasse. Brukes både som klassifiseringsresultat og som menneskets
 *  vurdering av et bilde — samme vokabular, så de kan sammenlignes direkte. */
export const UgsMalklasse = z.enum([
    'person-til-fots',
    'person-lopende',
    'hjul',
    'belte',
    'dyr',
    'ukjent',
]);
/** Grov lydklasse fra en akustisk UGS. Speiler SPARK sin `DetectionType`
 *  semantisk (se `lydklasseTilSparkType()`) — SPARK er wire-fasiten (§3), vi
 *  lager ikke en parallell modell for lyd i luft. */
export const UgsLydklasse = z.enum([
    'skritt',
    'stemme',
    'kjøretøy-diesel',
    'kjøretøy-bensin',
    'beltestøy',
    'rotor',
    'skudd',
    'ukjent',
]);
/**
 * Amplitudens enhet. MÅ oppgis, av samme grunn som hydrofonens nivå bærer
 * referansen i navnet: «amplitude 1800» er meningsløst. `rå-teller` er ADC-verdi
 * uten kalibrering, og da er tallet BARE sammenlignbart med samme sensors egne
 * tidligere utløsninger — aldri mellom to sensorer, og aldri mot en terskel i
 * mm/s. Klassifiseringen bruker derfor amplitude bare når enheten er fysisk.
 */
export const AmplitudeEnhet = z.enum(['rå-teller', 'mm-per-s', 'g', 'nT', 'binær']);
// Fellesfeltene. Skrevet som objekt-literal og spredt inn i hver variant, fordi
// z.discriminatedUnion krever rene ZodObject-medlemmer (samme grep som
// `deteksjonFelles` i hydrofon.ts).
const utlosningFelles = {
    utlosningId: z.string().min(1).max(64),
    sensorId: z.string().min(1).max(64),
    /**
     * Tidspunkt, ms siden epoch, DESIMALER TILLATT. Desimaler fordi en
     * dobbelstråle-PIR skiller strålebrudd i titalls millisekunder, og fordi
     * heltallskvantisering ellers legger seg oppå klokkeslarken.
     */
    tidspunktMs: z.number(),
    /**
     * 1σ tidsusikkerhet i millisekunder. PÅKREVD, og `.positive()` — les punkt 1)
     * i filhodet. En UGS med drift i klokka ødelegger all sekvensanalyse, og det
     * er den viktigste feilkilden i hele appen. `0` er ikke «perfekt klokke», det
     * er en klokke ingen har målt: bruk et tall du kan forsvare (GPS-synk ~1 ms,
     * nettverkssynk titalls ms, fri RTC sekunder per døgn siden siste synk).
     */
    tidsusikkerhetMs: z.number().positive().max(86_400_000),
    /** Sekvensnummer fra noden, om den teller. Avdekker TAPTE utløsninger — et
     *  hull i rekka betyr at sekvensen du analyserer er ufullstendig. */
    seq: z.number().int().nonnegative().optional(),
    merknad: z.string().max(400).optional(),
};
/**
 * TERSKELUTLØSNING — seismisk, magnetisk, brytertråd.
 *
 * LEGG MERKE TIL HVA SOM IKKE ER HER: det finnes ikke noe retningsfelt. Det er
 * hele poenget med variantene. En geofon måler bakkens hastighet i ett punkt;
 * en person som går forbi 10 m nord gir samme utslag som en 10 m sør. Retning
 * finnes bare i REKKEFØLGEN over flere sensorer (`sekvensSpor()`).
 */
export const UgsUtlosningTerskel = z.object({
    maling: z.literal('terskel'),
    ...utlosningFelles,
    /** Toppamplitude i `amplitudeEnhet`. */
    amplitude: z.number().min(0),
    amplitudeEnhet: AmplitudeEnhet,
    /** Hvor lenge signalet var over terskel (ms). Skiller et enkelt skritt
     *  (hundre-talls ms) fra et kjøretøy som ruller forbi (sekunder). */
    varighetMs: z.number().min(0).max(3_600_000),
    /** Terskelen noden brukte, samme enhet. Uten den vet vi ikke om «ingen
     *  utløsning» betyr «ingen der» eller «terskelen står for høyt». */
    terskelverdi: z.number().min(0).optional(),
    /**
     * Estimert skrittrate (Hz) fra periodisiteten i signalets omhylling.
     * SVAKT LEDD — se `ugsKlassifiser()`. Oppgis bare når noden faktisk har
     * regnet den; den skal ALDRI gjettes fra varigheten.
     */
    skrittrateHz: z.number().positive().max(20).optional(),
    /** Dominerende frekvens i utslaget (Hz), om noden regner spekter. */
    dominantFrekvensHz: z.number().positive().max(2000).optional(),
});
/**
 * RETNINGSUTLØSNING — dobbelstråle-PIR og liknende. Den ENESTE varianten som
 * bærer retning fra én sensor, og bare fordi den fysisk har to stråler med kjent
 * innbyrdes plassering: rekkefølgen strålene brytes i ER retningen.
 *
 * Retningen uttrykkes som «med» eller «mot» en AKSE, ikke som en kurs i grader.
 * Grunnen er at sensoren ikke måler en kurs — den måler et fortegn. Å skrive
 * «kurs 095°» ville gitt en presisjon som ikke finnes; aksen pluss fortegn er
 * presis nok, og lyver ikke.
 */
export const UgsUtlosningRetning = z.object({
    maling: z.literal('retning'),
    ...utlosningFelles,
    /** Aksens kurs (grader fra nord): fra stråle 1 mot stråle 2. */
    aksekursDeg: z.number().min(0).max(360),
    /** 1σ på aksekursen — den er innmålt med kompass, ikke med teodolitt. */
    aksekursUsikkerhetDeg: z.number().min(0).max(90).default(0),
    /** `med-akse` = brøt stråle 1 først (beveget seg langs aksekursen).
     *  `mot-akse` = motsatt vei. */
    passering: z.enum(['med-akse', 'mot-akse']),
    /** Avstand mellom strålene (m), om kjent. */
    straleAvstandM: z.number().positive().max(100).optional(),
    /** Tid mellom de to strålebruddene (ms). Sammen med `straleAvstandM` gir det
     *  en LOKAL fart ved sensoren — uavhengig av sekvensanalysen, og et godt
     *  krysssjekk mot den. */
    straleMellomtidMs: z.number().positive().max(600_000).optional(),
    /** Antall passeringer i samme utløsningsvindu, om noden teller. */
    antallPasseringer: z.number().int().min(1).max(100).optional(),
});
/**
 * LYDUTLØSNING — akustisk UGS. Nivået bærer LUFTREFERANSEN i navnet
 * (`nivaDbRe20uPa`), ikke marinreferansen 1 µPa som hydrofon.ts bruker. De to
 * skiller 26 dB, og et felt som bare het `nivaDb` ville blitt lest med feil
 * referanse første gang noen sammenlignet en UGS med en hydrofon.
 * Ingen peiling: én mikrofon er omnidireksjonell (hydrofon.ts punkt 2).
 */
export const UgsUtlosningLyd = z.object({
    maling: z.literal('lyd'),
    ...utlosningFelles,
    lydklasse: UgsLydklasse,
    /** 0–1 på LYDKLASSEN (ikke på målklassen). */
    klassekonfidens: z.number().min(0).max(1),
    /** Analysebåndet nivået gjelder for. PÅKREVD av samme grunn som i
     *  hydrofon.ts: et nivå uten bånd kan ikke sammenlignes med noe. */
    bandFraHz: z.number().min(0).max(200_000),
    bandTilHz: z.number().positive().max(200_000),
    /** Mottatt nivå, dB re 20 µPa (luft). */
    nivaDbRe20uPa: z.number().optional(),
    /** Målt bakgrunn i SAMME bånd — grunnlaget for SNR. */
    stoyGulvDbRe20uPa: z.number().optional(),
    dominantFrekvensHz: z.number().positive().max(200_000).optional(),
    varighetMs: z.number().min(0).max(3_600_000).optional(),
});
/**
 * BILDEUTLØSNING — viltkamera. Operativt den STERKESTE UGS-en vi har, men bare
 * etter at et menneske har sett bildet: da er klassifiseringen en observasjon og
 * ikke en slutningskjede. Før det er den bare «kameraet utløste».
 * `vurdertAvMenneske` er derfor et påkrevd felt, ikke en valgfri flagg — se
 * `ugsKlassifiser()` for hvordan konfidensen hopper når den er true.
 */
export const UgsUtlosningBilde = z.object({
    maling: z.literal('bilde'),
    ...utlosningFelles,
    /** Opak media-id. IKKE URL — se `BildeReferanse`. */
    bildeId: BildeReferanse,
    antallBilder: z.number().int().min(1).max(100).default(1),
    utlosningsarsak: BildeArsak,
    /** Har et menneske SETT bildet? Default false — og det er den ærlige
     *  starttilstanden: bildet ligger i køen, ingen har åpnet det. */
    vurdertAvMenneske: z.boolean().default(false),
    /** Menneskets vurdering. Krever `vurdertAvMenneske: true` (håndhevet under). */
    menneskeligVurdering: UgsMalklasse.optional(),
    /** Hvem som vurderte. Sporbarhet — en vurdering uten navn kan ikke etterprøves. */
    vurdertAv: z.string().max(64).optional(),
    /** Lysforhold. Avgjør hvor mye et bilde er verdt: IR-blits på 15 m i regn gir
     *  en hvit flekk, og «person» fra en slik flekk er en gjetning. */
    lysforhold: z.enum(['dagslys', 'skumring', 'ir-blits', 'natt-uten-lys']).optional(),
});
const utlosningUnion = z.discriminatedUnion('maling', [
    UgsUtlosningTerskel,
    UgsUtlosningRetning,
    UgsUtlosningLyd,
    UgsUtlosningBilde,
]);
export const UgsUtlosningSkjema = utlosningUnion.superRefine((u, ctx) => {
    if (u.maling === 'lyd' && u.bandTilHz <= u.bandFraHz) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['bandTilHz'],
            message: 'bandTilHz må være større enn bandFraHz',
        });
    }
    if (u.maling === 'bilde' && u.menneskeligVurdering !== undefined && !u.vurdertAvMenneske) {
        // En vurdering uten at noen har sett bildet er en maskinklassifisering som
        // utgir seg for å være en observasjon. Det er nøyaktig den forvekslingen
        // konfidensen i `ugsKlassifiser()` hviler på, så den må avvises hardt.
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['menneskeligVurdering'],
            message: 'menneskeligVurdering krever vurdertAvMenneske: true — ellers er det ikke et menneskes observasjon',
        });
    }
    if (u.maling === 'retning' && u.straleAvstandM !== undefined && u.straleMellomtidMs === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['straleMellomtidMs'],
            message: 'straleAvstandM uten straleMellomtidMs gir ingen fart — oppgi begge eller ingen',
        });
    }
    if (u.maling === 'retning' && u.straleMellomtidMs !== undefined && u.straleAvstandM === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['straleAvstandM'],
            message: 'straleMellomtidMs uten straleAvstandM gir ingen fart — oppgi begge eller ingen',
        });
    }
});
/** Parse én sensor. Null ved ugyldig (aldri kast) — som parseHydrofonPost. */
export function parseUgsSensor(raw) {
    const p = UgsSensorSkjema.safeParse(raw);
    return p.success ? p.data : null;
}
/** Parse én utløsning. Null ved ugyldig. */
export function parseUgsUtlosning(raw) {
    const p = UgsUtlosningSkjema.safeParse(raw);
    return p.success ? p.data : null;
}
// ── Wire-konvolutt ──────────────────────────────────────────────────────────
export const UGS_KANAL = 'ugs';
export const UgsMelding = z.discriminatedUnion('type', [
    z.object({ type: z.literal('sensor'), sensor: UgsSensorSkjema }),
    z.object({ type: z.literal('utlosning'), utlosning: UgsUtlosningSkjema }),
    z.object({
        type: z.literal('status'),
        sensorId: z.string().min(1),
        batteri: UgsBatteri.optional(),
        lenke: UgsLenke.optional(),
        tsMs: z.number().int().nonnegative(),
    }),
    z.object({
        type: z.literal('borte'),
        sensorId: z.string().min(1),
        /** Hvorfor kontakten falt. Vises, aldri skjult (som radar.ts/hydrofon.ts).
         *  For en UGS er dette dessuten et OPSEC-signal: en sensor som blir stille
         *  MIDT i et område med aktivitet kan være funnet og fjernet. */
        arsak: z.string().max(200),
    }),
    z.object({
        type: z.literal('fjernet'),
        sensorId: z.string().min(1),
        tsMs: z.number().int().nonnegative(),
        /** Hvem som hentet den. */
        av: z.string().max(64).optional(),
    }),
]);
// ── Geometri-hjelpere ───────────────────────────────────────────────────────
function norm360(d) {
    return ((d % 360) + 360) % 360;
}
/** Absolutt vinkelforskjell mellom to kurser, 0–180°. */
export function vinkelDiffDeg(a, b) {
    const d = Math.abs(norm360(a) - norm360(b)) % 360;
    return d > 180 ? 360 - d : d;
}
/**
 * Kurs (forward azimuth) fra a til b, grader fra nord.
 *
 * geo.ts har `project()` (fram) og `haversineM()` (avstand), men IKKE den
 * inverse retningen. Den regnes her, med SAMME sfæriske jordmodell, slik at
 * `project(a, kursDegMellom(a,b), haversineM(a,b)) ≈ b`. Å hente inn en annen
 * jordmodell for dette ville gitt to litt ulike sannheter i samme fil — den
 * feilen er gjort før (se kommentaren i hydrofon.ts om `lagPlan`).
 */
export function kursDegMellom(a, b) {
    const rad = Math.PI / 180;
    const lat1 = a.lat * rad;
    const lat2 = b.lat * rad;
    const dLon = (b.lon - a.lon) * rad;
    const y = Math.sin(dLon) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    return norm360((Math.atan2(y, x) * 180) / Math.PI);
}
/**
 * Korteste avstand fra punkt til linjesegment (m), regnet i et lokalt
 * øst/nord-plan om segmentets startpunkt. Samme jordradius som geo.ts.
 * Gyldig for de tiendedeler av en kilometer en brytertråd og en rutebein er.
 */
function segmentAvstandM(p, a, b) {
    const mPerDegLat = (Math.PI / 180) * 6_371_000;
    const mPerDegLon = mPerDegLat * Math.cos((a.lat * Math.PI) / 180);
    const px = (p.lon - a.lon) * mPerDegLon;
    const py = (p.lat - a.lat) * mPerDegLat;
    const bx = (b.lon - a.lon) * mPerDegLon;
    const by = (b.lat - a.lat) * mPerDegLat;
    const len2 = bx * bx + by * by;
    if (len2 <= 0)
        return Math.hypot(px, py);
    let t = (px * bx + py * by) / len2;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - t * bx, py - t * by);
}
/**
 * Hvor bred dekning en brytertråd har PÅ TVERS av seg selv, meter.
 * UVERIFISERT, men den fysiske sannheten er at den er nær null: man tråkker på
 * snoren eller ikke. 1,5 m er valgt som en snill tolkning (snoren henger, man
 * drar den med foten). Konsekvensen står i `dekningshull()`: en tråd lukker
 * nesten ingenting av et hull, men den som FØLGER ruta må krysse den.
 */
export const BRYTERTRAD_DEKNING_M = 1.5;
/** Sensorens posisjon som LatLon. */
export function sensorPos(s) {
    return { lat: s.lat, lon: s.lon };
}
/**
 * 1σ posisjonsfeil for sensoren (m): målt verdi om den finnes, ellers tabellen
 * for metoden. Denne går inn i BÅDE fartsintervallet og `cepM`.
 */
export function posisjonUsikkerhetM(s) {
    return s.posisjonUsikkerhetM ?? POSISJONSFEIL_M_UVERIFISERT[s.posisjonMetode];
}
/**
 * Største avstand fra sensorposisjonen der deteksjon er mulig (m). For en
 * brytertråd er det halve trådlengden — enden av tråden er det fjerneste
 * punktet den kan utløses på.
 */
export function dekningsradiusM(s) {
    const g = s.geometri;
    if (g.form === 'linje')
        return g.lengdeM / 2;
    return g.radiusM;
}
/**
 * Dekker denne sensoren dette punktet? Ren geometri — TERRENG ER IKKE MODELLERT,
 * og det er den største svakheten i `dekningshull()`. En rygg mellom sensor og
 * punkt stopper PIR og kamera fullstendig, og myr demper seismikk. Sikt- og
 * dempningsmodell hører i `@skade/terrain`, ikke i kontrakten; her er radien
 * operatørens EGEN vurdering av rekkevidde i det terrenget.
 *
 * NAVN (SENS-26): het tidligere `dekkerPunkt`, samme navn som funksjonen i
 * `ptz.ts`. IKKE samme logikk — dette er en STATISK sensors faste
 * dekningsgeometri (rundstrålende/sektor/linje, ingen tidsdimensjon), mot
 * `ptz.ts` sin sjekk av kameraets NÅVÆRENDE pekeretning (avhenger av
 * tilstand/alder/bevegelse). Ulik returtype (`boolean` her, rikt
 * `DekningsSvar` i `ptz.ts`) bekrefter det. Entydig navn her forhindrer at en
 * fremtidig barrel-eksport av `ptz.ts` (SENS-29) skygger denne i stillhet.
 */
export function dekkerPunktUgs(s, p) {
    const g = s.geometri;
    const pos = sensorPos(s);
    if (g.form === 'linje') {
        const halv = g.lengdeM / 2;
        const a = project(pos, g.retningDeg, halv);
        const b = project(pos, g.retningDeg + 180, halv);
        return segmentAvstandM(p, a, b) <= BRYTERTRAD_DEKNING_M;
    }
    const d = haversineM(pos, p);
    if (d > g.radiusM)
        return false;
    if (g.form === 'sektor') {
        if (g.apningsvinkelDeg >= 360)
            return true;
        // Rett på sensoren er retningen udefinert; da regnes punktet som dekket
        // (sensoren står på det).
        if (d < 0.5)
            return true;
        return vinkelDiffDeg(kursDegMellom(pos, p), g.retningDeg) <= g.apningsvinkelDeg / 2;
    }
    return true;
}
/**
 * Foreslått eksponeringsrisiko når operatøren ikke har satt den selv.
 * UVERIFISERT heuristikk, og med vilje PESSIMISTISK: det koster lite å flytte en
 * sensor, og mye å bli funnet. En brytertråd over en sti finnes av den første
 * som går der; et kamera med IR-blits lyser rødt i mørket for alle som ser mot
 * det. Operatørens egen vurdering på stedet vinner alltid over denne.
 */
export function antattEksponeringsrisiko(s) {
    if (s.eksponeringsrisiko)
        return s.eksponeringsrisiko;
    if (s.skjultGrad === 'synlig')
        return 'høy';
    if (s.art === 'brytertråd')
        return s.skjultGrad === 'godt-skjult' ? 'middels' : 'høy';
    if (s.art === 'kamera')
        return s.skjultGrad === 'godt-skjult' ? 'middels' : 'høy';
    return s.skjultGrad === 'godt-skjult' ? 'lav' : 'middels';
}
/**
 * UGS-lydklasse → SPARK sin `DetectionType`. Finnes for at en akustisk UGS skal
 * kunne meldes på GNÅ/SPARK-wiren uten at vi lager en PARALLELL modell for lyd i
 * luft (grunnloven §3: én kilde til sannhet, GNÅ er fasit for felt-wire).
 * `ukjent` returnerer undefined — SPARK har ingen «unknown», og å mappe den til
 * en tilfeldig type ville produsert falske deteksjonstyper i kartet.
 */
export function lydklasseTilSparkType(k) {
    switch (k) {
        case 'skritt':
        case 'stemme':
            return 'person';
        case 'kjøretøy-diesel':
        case 'kjøretøy-bensin':
        case 'beltestøy':
            return 'vehicle';
        case 'rotor':
            return 'drone';
        case 'skudd':
            return 'gunshot';
        case 'ukjent':
            return undefined;
    }
}
/**
 * Krysssjekk utløsning mot sensor. Kan ikke gjøres i skjemaet, fordi sensor og
 * utløsning er to ulike meldinger som kommer hver for seg — dette er UGS-ets
 * ekvivalent til hydrofonens type/array-refine. Returnerer lesbare problemer,
 * ikke booleans: de går rett inn i `advarsler` og videre til operatøren.
 */
export function validerUtlosningMotSensor(s, u) {
    const feil = [];
    if (u.sensorId !== s.sensorId) {
        feil.push(`utløsningen peker på sensor «${u.sensorId}», men sensoren er «${s.sensorId}»`);
    }
    if (u.maling === 'retning' && s.geometri.form === 'rundstrålende') {
        feil.push(`sensor ${s.sensorId} er rundstrålende, men melder passeringsretning — en rundstrålende sensor kan ikke fastslå retning (krever to stråler med kjent plassering)`);
    }
    if (u.maling === 'bilde' && s.art !== 'kamera') {
        feil.push(`sensor ${s.sensorId} har art «${s.art}» men melder bilde`);
    }
    if (u.maling === 'lyd' && s.art !== 'akustisk') {
        feil.push(`sensor ${s.sensorId} har art «${s.art}» men melder lydinnhold`);
    }
    if (u.maling === 'terskel' && (s.art === 'kamera' || s.art === 'akustisk')) {
        feil.push(`sensor ${s.sensorId} har art «${s.art}» men melder en ren terskelutløsning`);
    }
    if (u.tidspunktMs < s.utlagtMs) {
        feil.push(`utløsningen er datert FØR sensoren ble lagt ut — klokkefeil i noden eller feil sensorId`);
    }
    if (s.fjernetMs !== undefined && u.tidspunktMs > s.fjernetMs) {
        feil.push('utløsningen er datert ETTER at sensoren ble hentet inn');
    }
    return feil;
}
// ── Klassifisering ──────────────────────────────────────────────────────────
/**
 * TERSKLER FOR KLASSIFISERING — ALLE UVERIFISERT.
 *
 * De er hentet fra ganglags-litteratur (voksen gange ~1,5–2 skritt/s, løp over
 * ~2,5) og fra hvor lenge et kjøretøy rister bakken. Ingen av dem er målt med
 * eget utstyr i eget terreng, og de skal kalibreres mot egne opptak før de
 * brukes til noe som utløser handling.
 */
export const SKRITTRATE_GANGE_MIN_HZ = 1.2;
export const SKRITTRATE_GANGE_MAKS_HZ = 2.3;
export const SKRITTRATE_LOP_MIN_HZ = 2.4;
export const SKRITTRATE_LOP_MAKS_HZ = 4.5;
/** Over dette regnes utslaget som «ruller/går kontinuerlig», ikke enkeltskritt. */
export const VARIGHET_KJORETOY_MS = 6_000;
/** Kalibrert bakkehastighet over dette regnes som et TUNGT mål. Bare brukbar
 *  når `amplitudeEnhet` er fysisk (mm-per-s) — rå ADC-tellere kan ikke
 *  sammenlignes mot en terskel i det hele tatt. */
export const TUNGT_MAL_MM_PER_S = 0.5;
/**
 * HARDT TAK på konfidens fra seismikk/magnetisme alene. Les hvorfor:
 *
 * Kjeden er spektralinnhold → periodisitet i omhyllingen → skrittfrekvens →
 * «person». HVERT ledd er en antakelse:
 *  - amplituden avhenger MER av koblingen mellom fot og bakke (myr vs. berg) og
 *    av avstanden enn av målets masse,
 *  - periodisiteten forsvinner i ujevnt terreng der ingen går i takt,
 *  - og selv med perfekt skrittrate: en ELG i tungt terreng gir samme signatur
 *    som en person. Fire bein i ujevn takt ser i omhyllingen ut som to bein i
 *    jevn takt, og elgen veier mer, altså gir den STERKERE utslag — som lett
 *    leses som «flere personer».
 * Derfor 0,45: nok til å vise en klasse, aldri nok til å handle på alene. Skal
 * det over, må det ligge et BILDE eller et menneskeøye bak.
 */
export const MAKS_KONFIDENS_SEISMISK = 0.45;
/** Kamerautløsning som ingen har åpnet ennå. Bildet finnes, men ingen vet hva
 *  som er på det — konfidensen skal reflektere det, ikke bildets potensial. */
export const MAKS_KONFIDENS_UVURDERT_BILDE = 0.2;
/** Et menneske har sett bildet og sagt hva det er. Det er en OBSERVASJON, ikke
 *  en slutning — den eneste UGS-verdien som fortjener høy konfidens. */
export const KONFIDENS_VURDERT_BILDE = 0.9;
export const UgsKlassifiseringSkjema = z.object({
    klasse: UgsMalklasse,
    /** 0–1. Hold den lav, og les `MAKS_KONFIDENS_SEISMISK`. */
    konfidens: z.number().min(0).max(1),
    /** Andre klasser som passer nesten like godt. Vi RANGERER, vi utelukker ikke
     *  (samme valg som `HydrofonSignatur.alternativeKlasser`). */
    alternativer: z.array(UgsMalklasse).max(5).default([]),
    /** Hvorfor. SKAL vises ved siden av klassen — en klasse uten begrunnelse er en
     *  påstand operatøren ikke kan etterprøve. */
    begrunnelse: z.string().max(400),
    /** Hva slutningen bygger på. `menneske` er kvalitativt noe annet enn resten. */
    grunnlag: z.enum(['menneske', 'skrittrate', 'varighet', 'amplitude', 'lydklasse', 'passeringsfart', 'ingen']),
});
/** Lokal fart ved en dobbelstråle-sensor (m/s), om begge feltene finnes. */
export function passeringsfartMs(u) {
    if (u.maling !== 'retning')
        return undefined;
    if (u.straleAvstandM === undefined || u.straleMellomtidMs === undefined)
        return undefined;
    return u.straleAvstandM / (u.straleMellomtidMs / 1000);
}
/**
 * Klassifiser ÉN utløsning. Merk hva den ikke gjør: den gjetter ikke retning, og
 * den låner ikke konfidens fra sekvensen. Sekvensen behandles i `sekvensSpor()`.
 */
export function ugsKlassifiser(u, sensor) {
    const ukjent = (hvorfor, grunnlag = 'ingen') => ({
        klasse: 'ukjent',
        konfidens: 0.1,
        alternativer: [],
        begrunnelse: hvorfor,
        grunnlag,
    });
    switch (u.maling) {
        case 'bilde': {
            if (u.vurdertAvMenneske && u.menneskeligVurdering) {
                const mork = u.lysforhold === 'ir-blits' || u.lysforhold === 'natt-uten-lys';
                return {
                    klasse: u.menneskeligVurdering,
                    // IR-blits i mørket gir ofte en hvit flekk. Da er det fortsatt et
                    // menneske som har sett, men på et dårligere bilde — vi trekker fra,
                    // i stedet for å late som lysforholdene ikke betyr noe.
                    konfidens: mork ? KONFIDENS_VURDERT_BILDE - 0.2 : KONFIDENS_VURDERT_BILDE,
                    alternativer: [],
                    begrunnelse: `bilde ${u.bildeId} vurdert av ${u.vurdertAv ?? 'operatør'}${mork ? ' (dårlige lysforhold — IR/natt)' : ''}`,
                    grunnlag: 'menneske',
                };
            }
            if (u.vurdertAvMenneske) {
                return {
                    klasse: 'ukjent',
                    konfidens: 0.3,
                    alternativer: [],
                    begrunnelse: `bilde ${u.bildeId} er SETT, men et menneske klarte ikke å avgjøre hva det var — det er en reell observasjon av «uavklart», ikke manglende arbeid`,
                    grunnlag: 'menneske',
                };
            }
            return {
                klasse: 'ukjent',
                konfidens: MAKS_KONFIDENS_UVURDERT_BILDE,
                alternativer: [],
                begrunnelse: `bilde ${u.bildeId} (${u.utlosningsarsak}) er IKKE vurdert av menneske ennå — kameraet utløste, innholdet er ukjent`,
                grunnlag: 'ingen',
            };
        }
        case 'lyd': {
            const k = u.klassekonfidens;
            const par = {
                skritt: { klasse: 'person-til-fots', alt: ['dyr', 'person-lopende'] },
                stemme: { klasse: 'person-til-fots', alt: [] },
                'kjøretøy-diesel': { klasse: 'hjul', alt: ['belte'] },
                'kjøretøy-bensin': { klasse: 'hjul', alt: [] },
                beltestøy: { klasse: 'belte', alt: ['hjul'] },
                rotor: { klasse: 'ukjent', alt: [] },
                skudd: { klasse: 'ukjent', alt: ['person-til-fots'] },
                ukjent: { klasse: 'ukjent', alt: [] },
            };
            const m = par[u.lydklasse];
            if (m.klasse === 'ukjent') {
                return ukjent(`lydklasse «${u.lydklasse}» har ingen bakkemålklasse (rotor er luft, skudd er en hendelse — ikke et kjøremønster)`, 'lydklasse');
            }
            // Konfidensen ARVES fra nodens egen lydklassekonfidens og kappes: en
            // stemme er nesten sikker person, mens «skritt» i løvskog like godt er
            // regn på blader eller et dyr.
            const tak = u.lydklasse === 'stemme' ? 0.75 : 0.5;
            return {
                klasse: m.klasse,
                konfidens: Math.min(k, tak),
                alternativer: m.alt,
                begrunnelse: `akustisk klasse «${u.lydklasse}» (nodekonfidens ${k.toFixed(2)}, kappet på ${tak.toFixed(2)}) i båndet ${Math.round(u.bandFraHz)}–${Math.round(u.bandTilHz)} Hz`,
                grunnlag: 'lydklasse',
            };
        }
        case 'retning': {
            const fart = passeringsfartMs(u);
            if (fart === undefined) {
                return ukjent('dobbelstråle-passering gir RETNING, men ingenting om HVA som passerte — uten strålefart finnes ikke noe holdepunkt for klasse');
            }
            // Fart alene skiller gange fra løp fra kjøretøy, og ikke stort mer. En
            // syklist og en løpende person ligger i samme intervall.
            if (fart > 8) {
                return {
                    klasse: 'hjul',
                    konfidens: 0.3,
                    alternativer: ['belte', 'ukjent'],
                    begrunnelse: `passeringsfart ${fart.toFixed(1)} m/s er for høy for løping — kjøretøy, men arten kan ikke skilles`,
                    grunnlag: 'passeringsfart',
                };
            }
            if (fart > 2.6) {
                return {
                    klasse: 'person-lopende',
                    konfidens: 0.3,
                    alternativer: ['hjul', 'dyr'],
                    begrunnelse: `passeringsfart ${fart.toFixed(1)} m/s tilsvarer løp — men en syklist og et dyr i samme fart ser identisk ut for to IR-stråler`,
                    grunnlag: 'passeringsfart',
                };
            }
            return {
                klasse: 'person-til-fots',
                konfidens: 0.3,
                alternativer: ['dyr'],
                begrunnelse: `passeringsfart ${fart.toFixed(1)} m/s tilsvarer gange`,
                grunnlag: 'passeringsfart',
            };
        }
        case 'terskel': {
            const art = sensor?.art;
            // Et magnetometer sier «jern i bevegelse». Det er en sterkere indikasjon
            // på KJØRETØY enn seismikk er på person, fordi bakgrunnen (naturlig jern i
            // bevegelse) er nær null i skog. Men en person med våpen utløser den også.
            if (art === 'magnetisk') {
                const tungt = u.amplitudeEnhet === 'nT' && u.amplitude >= 50; // UVERIFISERT
                return {
                    klasse: tungt ? 'hjul' : 'ukjent',
                    konfidens: tungt ? 0.35 : 0.2,
                    alternativer: tungt ? ['belte', 'person-til-fots'] : ['person-til-fots', 'hjul'],
                    begrunnelse: tungt
                        ? `magnetisk utslag ${u.amplitude} nT er stort — mest sannsynlig kjøretøymasse, men et gruppevåpen som bæres forbi gir samme utslag (UVERIFISERT terskel 50 nT)`
                        : `magnetisk utslag ${u.amplitude} ${u.amplitudeEnhet} betyr «jern i bevegelse» og ingenting mer — verken masse eller retning`,
                    grunnlag: 'amplitude',
                };
            }
            if (art === 'brytertråd' || u.amplitudeEnhet === 'binær') {
                return ukjent('brytertråd sier at NOE brøt tråden på dette punktet, klokka da. Den bærer ingen signatur, altså ingen klasse — men posisjonen er den skarpeste vi har');
            }
            // Herfra: seismisk. Rekkefølgen under er valgt slik at det STERKESTE
            // holdepunktet vinner: skrittrate (målt periodisitet) foran varighet
            // (kontinuerlig rulling) foran amplitude (nesten bare avstand og kobling).
            if (u.skrittrateHz !== undefined) {
                const f = u.skrittrateHz;
                if (f >= SKRITTRATE_LOP_MIN_HZ && f <= SKRITTRATE_LOP_MAKS_HZ) {
                    return {
                        klasse: 'person-lopende',
                        konfidens: Math.min(0.4, MAKS_KONFIDENS_SEISMISK),
                        alternativer: ['person-til-fots', 'dyr'],
                        begrunnelse: `skrittrate ${f.toFixed(2)} Hz ligger i løpsintervallet ${SKRITTRATE_LOP_MIN_HZ}–${SKRITTRATE_LOP_MAKS_HZ} Hz (UVERIFISERT). Et dyr i trav gir samme rate`,
                        grunnlag: 'skrittrate',
                    };
                }
                if (f >= SKRITTRATE_GANGE_MIN_HZ && f <= SKRITTRATE_GANGE_MAKS_HZ) {
                    return {
                        klasse: 'person-til-fots',
                        konfidens: MAKS_KONFIDENS_SEISMISK,
                        alternativer: ['dyr', 'person-lopende'],
                        begrunnelse: `skrittrate ${f.toFixed(2)} Hz ligger i gangintervallet ${SKRITTRATE_GANGE_MIN_HZ}–${SKRITTRATE_GANGE_MAKS_HZ} Hz (UVERIFISERT). MERK: en elg i samme terreng gir samme rate og STERKERE utslag — «dyr» er ikke et fjernt alternativ, det er det mest sannsynlige alternativet i skog`,
                        grunnlag: 'skrittrate',
                    };
                }
                return ukjent(`skrittrate ${f.toFixed(2)} Hz faller utenfor både gange (${SKRITTRATE_GANGE_MIN_HZ}–${SKRITTRATE_GANGE_MAKS_HZ}) og løp (${SKRITTRATE_LOP_MIN_HZ}–${SKRITTRATE_LOP_MAKS_HZ}) — periodisiteten er trolig feiltolket`, 'skrittrate');
            }
            if (u.varighetMs >= VARIGHET_KJORETOY_MS) {
                return {
                    klasse: 'hjul',
                    konfidens: 0.3,
                    alternativer: ['belte', 'ukjent'],
                    begrunnelse: `sammenhengende utslag i ${(u.varighetMs / 1000).toFixed(1)} s (over ${VARIGHET_KJORETOY_MS / 1000} s, UVERIFISERT) peker på rullende kjøretøy og ikke enkeltskritt — men vind i et tre ved sensoren gir også langvarig rist`,
                    grunnlag: 'varighet',
                };
            }
            if (u.amplitudeEnhet === 'mm-per-s' && u.amplitude >= TUNGT_MAL_MM_PER_S) {
                return {
                    klasse: 'ukjent',
                    konfidens: 0.2,
                    alternativer: ['hjul', 'belte', 'dyr'],
                    begrunnelse: `kalibrert utslag ${u.amplitude.toFixed(2)} mm/s er stort (over ${TUNGT_MAL_MM_PER_S} mm/s, UVERIFISERT) ⇒ tungt mål ELLER nært mål. Amplitude alene kan ikke skille masse fra avstand`,
                    grunnlag: 'amplitude',
                };
            }
            if (u.amplitudeEnhet === 'rå-teller') {
                return ukjent(`amplitude ${u.amplitude} er rå ADC-teller uten kalibrering — den kan bare sammenlignes med SAMME sensors tidligere utløsninger, aldri mot en terskel eller en annen sensor`, 'amplitude');
            }
            return ukjent(`terskelutløsning på ${(u.varighetMs / 1000).toFixed(2)} s uten skrittrate — for kort for kjøretøy, for lite grunnlag for person`, 'varighet');
        }
    }
}
// ── Track-adaptere ──────────────────────────────────────────────────────────
/**
 * SIDC-er. UVERIFISERT, samme forbehold som hydrofon.ts: valgt etter
 * APP-6/2525B-logikk (posisjon 2 = tilhørighet, posisjon 3 = kampdimensjon
 * 'G' = bakke) i den 11-tegns legacy-formen resten av SKADE bruker
 * (jf. adapters.ts, cot.test.ts). Skal kontrolleres mot symbolbiblioteket.
 */
const SIDC_UKJENT_BAKKE = 'SUGP-------';
const SIDC_VENNLIG_SENSOR = 'SFGPEWS----'; // samme som SPARK-node/hydrofonpost
/** Den utlagte sensoren som vennlig sensor-spor. */
export function ugsSensorTilTrack(s, tsMs = 0) {
    const g = s.geometri;
    const batt = s.batteri.enhet === 'volt'
        ? `${s.batteri.spenningV.toFixed(2)} V (${s.batteri.kilde})`
        : `${s.batteri.prosent} % (${s.batteri.kilde})`;
    return {
        id: `ugs:${s.sensorId}`,
        kind: 'sensor',
        affiliation: 'friend',
        lat: s.lat,
        lon: s.lon,
        sidc: SIDC_VENNLIG_SENSOR,
        label: `UGS ${s.art.toUpperCase()} ${s.navn}`,
        tsMs,
        // Sensorens EGEN usikkerhet — ikke deteksjonsradien. Dette sporet er
        // sensoren, ikke en deteksjon.
        cepM: Math.round(posisjonUsikkerhetM(s)),
        source: `ugs:${s.sensorId}`,
        // Jf. SENSORGRAF §2.1: laget er echelonen, sensoren er nyttelasten.
        org: { team: s.lagId },
        ...(s.hoydeM !== undefined ? { hoydeM: s.hoydeM, hoydeReferanse: s.hoydeReferanse } : {}),
        // En ANSLÅTT posisjon er en estimert posisjon. At feltet finnes på Track
        // gjør at kartet kan tegne den annerledes uten å finne opp et eget flagg.
        estimated: s.posisjonMetode === 'anslått',
        extra: {
            art: s.art,
            lag: s.lagId,
            posisjon: `${s.posisjonMetode} (±${Math.round(posisjonUsikkerhetM(s))} m 1σ)`,
            dekning: g.form === 'linje'
                ? `tråd ${g.lengdeM} m i akse ${g.retningDeg}°`
                : g.form === 'sektor'
                    ? `sektor ${g.radiusM} m, ${g.retningDeg}° ±${(g.apningsvinkelDeg / 2).toFixed(0)}°`
                    : `radius ${g.radiusM} m`,
            batteri: batt,
            lenke: `${s.lenke.kvalitet}${s.lenke.hopp !== undefined ? `, ${s.lenke.hopp} hopp` : ''}`,
            skjult: s.skjultGrad,
            eksponering: `${antattEksponeringsrisiko(s)}${s.eksponeringsrisiko ? '' : ' (antatt)'}`,
            utlagt: `${s.utlagtAv} @ ${new Date(s.utlagtMs).toISOString()}`,
            klokke: s.klokkeKilde,
            ...(s.aktiv ? {} : { status: 'IKKE AKTIV — dekker ingenting' }),
        },
    };
}
/**
 * Utløsning → Track. HER ER DE GODE NYHETENE, og hvorfor de er lov:
 *
 * I motsetning til `hydrofonDeteksjonTilTrack()` — som må plassere sporet på
 * hydrofonen og nekte å sette `cepM`, fordi passiv akustikk ikke måler avstand —
 * VET vi hvor en UGS-utløsning skjedde: innenfor deteksjonsradien til en sensor
 * hvis posisjon vi kjenner. Derfor:
 *
 *  - posisjonen er sensorens, og det er en EKTE posisjon for målet (±radius),
 *  - `cepM` SETTES = deteksjonsradius + sensorens posisjonsfeil,
 *  - `estimated` er FALSE — med ett unntak: er sensorposisjonen selv `anslått`,
 *    er punktet en estimert posisjon, og da er `true` det ærlige svaret.
 *
 * cepM legges LINEÆRT, ikke i kvadratur: deteksjonsradien er en HARD GRENSE
 * («utenfor denne utløser den ikke»), mens posisjonsfeilen er en 1σ. Kvadratur
 * mellom en grense og et standardavvik underdriver alltid, og her ville
 * underdrivelsen gjort en sirkel på kartet mindre enn området målet faktisk var i.
 *
 * KLASSIFISERINGEN er derimot svak, og det skal fram: `confidence` kommer fra
 * `ugsKlassifiser()` (kappet på 0,45 for seismikk), `extra.begrunnelse` sier
 * hvorfor, og `extra.retning` sier eksplisitt at én utløsning IKKE bærer retning.
 *
 * Tilhørighet er `unknown`. En utløsning er ikke en fiende — det kan være en elg,
 * en bær-plukker eller egen patrulje som gikk feil. `hostile` her ville vært
 * automatisert fiendeerklæring fra en rystelse i bakken.
 */
export function ugsUtlosningTilTrack(s, u, klass) {
    const k = klass ?? ugsKlassifiser(u, s);
    const radius = dekningsradiusM(s);
    const posFeil = posisjonUsikkerhetM(s);
    const cepM = Math.round((radius + posFeil) * 10) / 10;
    const avvik = validerUtlosningMotSensor(s, u);
    const extra = {
        posisjon: `sensorens posisjon — målet var innenfor ${Math.round(radius)} m av den`,
        posisjonsmetode: `${s.posisjonMetode} (±${Math.round(posFeil)} m 1σ)`,
        tid: `${new Date(Math.round(u.tidspunktMs)).toISOString()} ±${(u.tidsusikkerhetMs / 1000).toFixed(3)} s (1σ)`,
        måling: u.maling,
        sensor: `${s.art} ${s.navn} (lag ${s.lagId})`,
        klasse: `${k.klasse} (konfidens ${k.konfidens.toFixed(2)}, grunnlag ${k.grunnlag})`,
        begrunnelse: k.begrunnelse,
    };
    if (k.alternativer.length > 0)
        extra['alternativer'] = k.alternativer.join(', ');
    if (u.maling === 'retning') {
        extra['retning'] = `passerte ${u.passering} ${u.aksekursDeg.toFixed(0)}°${u.aksekursUsikkerhetDeg > 0 ? ` ±${u.aksekursUsikkerhetDeg.toFixed(0)}°` : ''} (dobbelstråle)`;
        const f = passeringsfartMs(u);
        if (f !== undefined)
            extra['passeringsfart'] = `${f.toFixed(1)} m/s (lokalt, ved sensoren)`;
    }
    else {
        // Det viktigste ene feltet i hele adapteren.
        extra['retning'] = 'INGEN — én utløsning er et punkt, ikke en bevegelse. Retning og fart krever sekvensSpor() over flere sensorer';
    }
    switch (u.maling) {
        case 'terskel':
            extra['amplitude'] = `${u.amplitude} ${u.amplitudeEnhet}${u.amplitudeEnhet === 'rå-teller' ? ' (UKALIBRERT — kun sammenlignbar med samme sensor)' : ''}`;
            extra['varighet'] = `${(u.varighetMs / 1000).toFixed(2)} s`;
            if (u.skrittrateHz !== undefined)
                extra['skrittrate'] = `${u.skrittrateHz.toFixed(2)} Hz (UTLEDET av omhyllingen, ikke målt direkte)`;
            break;
        case 'lyd':
            extra['lydklasse'] = `${u.lydklasse} (nodekonfidens ${u.klassekonfidens.toFixed(2)})`;
            extra['bånd'] = `${Math.round(u.bandFraHz)}–${Math.round(u.bandTilHz)} Hz`;
            if (u.nivaDbRe20uPa !== undefined)
                extra['nivå'] = `${u.nivaDbRe20uPa.toFixed(1)} dB re 20 µPa (luft)`;
            if (u.nivaDbRe20uPa !== undefined && u.stoyGulvDbRe20uPa !== undefined) {
                extra['snr'] = `${(u.nivaDbRe20uPa - u.stoyGulvDbRe20uPa).toFixed(1)} dB (utledet)`;
            }
            break;
        case 'bilde':
            // Bare id-en. Ingen URL, ingen vertsnavn — klienten slår opp bak RBAC.
            extra['bilde'] = `id ${u.bildeId} (${u.antallBilder} stk, årsak ${u.utlosningsarsak})`;
            extra['vurdert'] = u.vurdertAvMenneske ? `JA${u.vurdertAv ? ` av ${u.vurdertAv}` : ''}` : 'NEI — ingen har sett bildet ennå';
            if (u.lysforhold)
                extra['lysforhold'] = u.lysforhold;
            break;
        case 'retning':
            if (u.antallPasseringer !== undefined)
                extra['passeringer'] = String(u.antallPasseringer);
            break;
    }
    if (s.lenke.kvalitet === 'tapt' || s.lenke.kvalitet === 'ingen-lenke') {
        extra['lenke'] = `${s.lenke.kvalitet} — utløsningen kan ha kommet SENT fram; tidspunktet er nodens, ikke mottakstiden`;
    }
    if (avvik.length > 0)
        extra['avvik'] = avvik.join(' | ');
    return {
        id: `ugs:${s.sensorId}:${u.utlosningId}`,
        kind: 'detection',
        affiliation: 'unknown',
        lat: s.lat,
        lon: s.lon,
        sidc: SIDC_UKJENT_BAKKE,
        label: `UGS ${k.klasse.toUpperCase()}`,
        tsMs: Math.round(u.tidspunktMs),
        cepM,
        confidence: k.konfidens,
        source: `ugs:${s.sensorId}`,
        org: { team: s.lagId },
        estimated: s.posisjonMetode === 'anslått',
        ...(s.hoydeM !== undefined ? { hoydeM: s.hoydeM, hoydeReferanse: s.hoydeReferanse } : {}),
        extra,
    };
}
/**
 * Sensorenes dekning som GeoJSON, til kartlaget. Sektor tegnes som sektor og
 * tråd som strek — aldri som disk. En dekningsflate som er større enn den
 * virkelige er den ene grafiske løgnen som får et lag til å tro at en akse er
 * dekket når den ikke er (jf. SENSORGRAF §2.1 c: postens dekning er UNIONEN av
 * sensorenes faktiske dekning, aldri en sirkel rundt posten).
 */
export function ugsDekningGeoJson(sensorer) {
    const ut = [];
    for (const s of sensorer.slice(0, MAX_SENSORER)) {
        const g = s.geometri;
        const pos = sensorPos(s);
        const props = { sensorId: s.sensorId, art: s.art, form: g.form, aktiv: s.aktiv };
        if (g.form === 'rundstrålende' || (g.form === 'sektor' && g.apningsvinkelDeg >= 360)) {
            ut.push({
                type: 'Feature',
                geometry: { type: 'Polygon', coordinates: [ringPolygon(pos, g.radiusM)] },
                properties: props,
            });
        }
        else if (g.form === 'sektor') {
            const ring = [[pos.lon, pos.lat]];
            const fra = g.retningDeg - g.apningsvinkelDeg / 2;
            const steg = Math.max(2, Math.ceil(g.apningsvinkelDeg / 5));
            for (let i = 0; i <= steg; i++) {
                const p = project(pos, fra + (g.apningsvinkelDeg * i) / steg, g.radiusM);
                ring.push([p.lon, p.lat]);
            }
            ring.push([pos.lon, pos.lat]);
            ut.push({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] }, properties: props });
        }
        else {
            const a = project(pos, g.retningDeg, g.lengdeM / 2);
            const b = project(pos, g.retningDeg + 180, g.lengdeM / 2);
            ut.push({
                type: 'Feature',
                geometry: { type: 'LineString', coordinates: [[a.lon, a.lat], [b.lon, b.lat]] },
                properties: props,
            });
        }
    }
    return featureCollection(ut);
}
// ── Sekvensanalyse: HOVEDFUNKSJONEN ─────────────────────────────────────────
/**
 * Fysisk øvre grense for et mål TIL FOTS, m/s. UVERIFISERT, men begrunnet:
 * verdensrekorden på 100 m er ~10,4 m/s i snitt på bane; i skog med oppakning
 * kommer ingen i nærheten. 15 m/s er satt med margin for at feilkildene våre
 * (radius + klokkeslark) skal få slippe til FØRST — vi vil at flagget «umulig
 * fart» skal bety «to ulike mål eller klokkefeil», ikke «usikkerheten er stor».
 */
export const MAKS_FART_TIL_FOTS_MS = 15;
/** Tilsvarende for kjøretøy på skogsvei/traktorvei. UVERIFISERT. */
export const MAKS_FART_KJORETOY_MS = 30;
/** Typisk marsjfart til fots i terreng, m/s (~5 km/t). UVERIFISERT. */
export const TYPISK_GANGFART_MS = 1.4;
/** Kursendring over dette regnes som en VENDING (målet snudde) og ikke som en
 *  sving på samme bevegelse. UVERIFISERT — 120° er valgt fordi en sti kan svinge
 *  90° uten at noen har snudd. */
export const VENDING_TERSKEL_DEG = 120;
/** Default maks opphold mellom to utløsninger for at de kan tilhøre SAMME
 *  bevegelse (ms). 5 minutter. UVERIFISERT og bevisst romslig: en patrulje som
 *  stopper og lytter i fire minutter er fortsatt samme patrulje. */
export const MAKS_OPPHOLD_MS = 300_000;
function fmt(n, d = 1) {
    return n.toFixed(d).replace('.', ',');
}
/**
 * SEKVENSSPOR — utled bevegelsesretning, fart og antatt antall passerende fra
 * utløsninger på FLERE sensorer.
 *
 * HVORFOR DENNE ER HOVEDFUNKSJONEN: en enkelt UGS-utløsning er en sterk posisjon
 * og en verdiløs retning. All kinematikk i ULL kommer herfra. Regnestykket er
 * trivielt (avstand/tid); det som ikke er trivielt, og som er hele grunnen til at
 * funksjonen er lang, er å ikke lyve om resultatet:
 *
 *  1. FARTEN ER ET INTERVALL. Avstandsslarken (deteksjonsradius + posisjonsfeil
 *     for begge sensorene) og tidsslarken (begge klokkene) propageres til
 *     `fartMinMs = (d−Δd)/(Δt+Δτ)` og `fartMaksMs = (d+Δd)/(Δt−Δτ)`. Med 80 m,
 *     ±20 m radius og ±2 s samlet slark blir intervallet 1,4–7,2 m/s. Det ER
 *     bredt, og bredden er informasjonen: den sier at sensorene står for tett
 *     for å måle fart.
 *  2. SLARKENE LEGGES LINEÆRT, IKKE I KVADRATUR. Kvadratur forutsetter
 *     uavhengige tilfeldige feil. Deteksjonsradien er en hard grense, og
 *     klokkedrift er SYSTEMATISK (en node som ligger 3 s bak, ligger 3 s bak
 *     hele døgnet). Kvadratur ville derfor systematisk underdrevet intervallet.
 *  3. NÅR Δt ≤ Δτ ER IKKE ENGANG REKKEFØLGEN BESTEMT. Da finnes ingen retning å
 *     rapportere, og `fartMaksMs` er ubegrenset (undefined). Dette er den
 *     feilen som er lettest å ikke oppdage: to sensorer 30 m fra hverandre og
 *     to noder med ±2 s RTC-slark KAN IKKE ordnes i tid i det hele tatt.
 *  4. URIMELIG FART FLAGGES BARE NÅR HELE INTERVALLET ER UMULIG. Flagger vi på
 *     midtverdien, roper vi ulv hver gang usikkerheten er stor. Er selv NEDRE
 *     grense over `maksFartMs`, er det derimot noe ekte: to ulike mål, eller en
 *     klokke som er feil. Begge er viktige, og begge sies.
 *  5. SAMME SENSOR TO GANGER ER IKKE BEVEGELSE. Avstanden er null, farten er
 *     udefinert, og tolkningen er «samme mål snudde/oppholder seg der» ELLER
 *     «to mål passerte samme punkt». Vi sier det, vi velger ikke.
 *  6. TVETYDIGHETEN GJETTES IKKE BORT. N uavhengige mål som utløser N sensorer i
 *     denne rekkefølgen gir presis samme datasett som ett mål i bevegelse. UGS
 *     har ingen målidentitet. Derfor rapporteres alltid
 *     `antallPasserendeMin/Antatt/Maks`, og `begrunnelse` sier hypotesen høyt.
 */
export function sekvensSpor(utlosninger, sensorer, valg = {}) {
    if (!Array.isArray(utlosninger) || !Array.isArray(sensorer))
        return { ok: false, arsak: 'ugyldige inndata' };
    if (utlosninger.length < 2) {
        return {
            ok: false,
            arsak: 'sekvensanalyse krever minst 2 utløsninger — én utløsning er et punkt i rom og tid, og bærer ingen retning eller fart (det er hele premisset for UGS)',
        };
    }
    if (utlosninger.length > MAX_UTLOSNINGER)
        return { ok: false, arsak: `for mange utløsninger (maks ${MAX_UTLOSNINGER})` };
    if (sensorer.length > MAX_SENSORER)
        return { ok: false, arsak: `for mange sensorer (maks ${MAX_SENSORER})` };
    const kart = new Map();
    for (const s of sensorer) {
        if (kart.has(s.sensorId))
            return { ok: false, arsak: `sensorId «${s.sensorId}» er registrert flere ganger` };
        kart.set(s.sensorId, s);
    }
    const advarsler = [];
    const settUtlosning = new Set();
    for (const u of utlosninger) {
        if (settUtlosning.has(u.utlosningId))
            return { ok: false, arsak: `utlosningId «${u.utlosningId}» er med flere ganger` };
        settUtlosning.add(u.utlosningId);
        if (!Number.isFinite(u.tidspunktMs))
            return { ok: false, arsak: `ugyldig tidspunkt for utløsning «${u.utlosningId}»` };
        const s = kart.get(u.sensorId);
        if (!s) {
            return {
                ok: false,
                arsak: `utløsning «${u.utlosningId}» viser til ukjent sensor «${u.sensorId}» — uten sensorposisjon finnes ingen geometri å regne på`,
            };
        }
        for (const f of validerUtlosningMotSensor(s, u))
            if (!advarsler.includes(f))
                advarsler.push(f);
        if (s.posisjonMetode === 'anslått') {
            advarsler.push(`sensor ${s.sensorId} har ANSLÅTT posisjon (±${Math.round(posisjonUsikkerhetM(s))} m) — alle avstander og fartsintervall som involverer den er tilsvarende svakere`);
        }
        if (s.klokkeKilde === 'intern-rtc' || s.klokkeKilde === 'ukjent') {
            advarsler.push(`sensor ${s.sensorId} har klokkekilde «${s.klokkeKilde}» — kontroller at tidsusikkerheten (${u.tidsusikkerhetMs} ms) faktisk dekker driften siden siste synk`);
        }
    }
    const maksOpphold = valg.maksOppholdMs ?? MAKS_OPPHOLD_MS;
    const maksFart = valg.maksFartMs ?? MAKS_FART_TIL_FOTS_MS;
    const ekstra = Math.max(0, valg.ekstraTidsslakkMs ?? 0);
    // Sorter i tid. Stabil sortering betyr at to utløsninger med IDENTISK tidspunkt
    // beholder inndatarekkefølgen — vi later ikke som vi vet hvilken som var først,
    // og `rekkefolgeSikker` under sier det eksplisitt.
    const rekke = utlosninger
        .map((u, i) => ({ u, i }))
        .sort((a, b) => (a.u.tidspunktMs - b.u.tidspunktMs) || (a.i - b.i))
        .map((x) => x.u);
    const begrunnelse = [];
    const sensorIder = new Set(rekke.map((u) => u.sensorId));
    const varighetS = (rekke[rekke.length - 1].tidspunktMs - rekke[0].tidspunktMs) / 1000;
    begrunnelse.push(`${rekke.length} utløsninger på ${sensorIder.size} sensor(er) over ${fmt(varighetS)} s (${new Date(Math.round(rekke[0].tidspunktMs)).toISOString()} → ${new Date(Math.round(rekke[rekke.length - 1].tidspunktMs)).toISOString()}).`);
    const bein = [];
    for (let i = 1; i < rekke.length; i++) {
        const a = rekke[i - 1];
        const b = rekke[i];
        const sa = kart.get(a.sensorId);
        const sb = kart.get(b.sensorId);
        const beinAdv = [];
        const avstandM = haversineM(sensorPos(sa), sensorPos(sb));
        const slakkM = dekningsradiusM(sa) + posisjonUsikkerhetM(sa) + dekningsradiusM(sb) + posisjonUsikkerhetM(sb);
        const dtMs = b.tidspunktMs - a.tidspunktMs;
        const tidsdiffS = dtMs / 1000;
        const tidsslakkS = (a.tidsusikkerhetMs + b.tidsusikkerhetMs + 2 * ekstra) / 1000;
        const sammeSensor = sa.sensorId === sb.sensorId;
        const bruddIKjede = dtMs > maksOpphold;
        const rekkefolgeSikker = Math.abs(tidsdiffS) > tidsslakkS;
        let kurs;
        let kursSlakk;
        if (!sammeSensor && avstandM > 0) {
            kurs = kursDegMellom(sensorPos(sa), sensorPos(sb));
            kursSlakk = avstandM <= slakkM ? 180 : (Math.atan(slakkM / avstandM) * 180) / Math.PI;
            if (kursSlakk >= 180) {
                beinAdv.push(`posisjonsslarken (±${Math.round(slakkM)} m) er større enn sensoravstanden (${Math.round(avstandM)} m) — retningen mellom ${sa.sensorId} og ${sb.sensorId} er IKKE bestemt`);
            }
        }
        let fartMin;
        let fartMaks;
        let fartMidt;
        if (sammeSensor) {
            beinAdv.push(`samme sensor (${sa.sensorId}) utløst på nytt etter ${fmt(Math.abs(tidsdiffS))} s — avstanden er null, altså finnes ingen fart. Tolkninger: samme mål som snudde eller oppholder seg i dekningsområdet, ELLER et nytt mål på samme punkt`);
        }
        else {
            const dMin = Math.max(0, avstandM - slakkM);
            const dMaks = avstandM + slakkM;
            const dtAbs = Math.abs(tidsdiffS);
            const dtMaks = dtAbs + tidsslakkS;
            const dtMin = dtAbs - tidsslakkS;
            fartMidt = dtAbs > 0 ? avstandM / dtAbs : undefined;
            fartMin = dMin / dtMaks;
            if (dtMin > 0) {
                fartMaks = dMaks / dtMin;
            }
            else {
                beinAdv.push(`tidsdifferansen (${fmt(dtAbs)} s) er ikke større enn den samlede klokkeslarken (±${fmt(tidsslakkS)} s) — verken rekkefølgen eller en øvre fartsgrense kan fastslås mellom ${sa.sensorId} og ${sb.sensorId}`);
            }
        }
        const urimeligFart = fartMin !== undefined && fartMin > maksFart;
        if (urimeligFart) {
            beinAdv.push(`selv NEDRE fartsgrense (${fmt(fartMin)} m/s) overstiger ${maksFart} m/s — dette kan ikke være ett mål i bevegelse. Enten er det to ULIKE mål, eller minst én av klokkene er feil`);
        }
        else if (fartMaks !== undefined && fartMaks > maksFart) {
            beinAdv.push(`øvre fartsgrense (${fmt(fartMaks)} m/s) er over ${maksFart} m/s, men nedre er ikke — usikkerheten alene forklarer det. Ikke flagget som umulig`);
        }
        if (bruddIKjede) {
            beinAdv.push(`${fmt(tidsdiffS)} s opphold overstiger ${maksOpphold / 1000} s — kjeden er BRUTT her, og de to sidene er sannsynligvis ulike hendelser`);
        }
        bein.push({
            fraSensorId: sa.sensorId,
            tilSensorId: sb.sensorId,
            fraUtlosningId: a.utlosningId,
            tilUtlosningId: b.utlosningId,
            avstandM,
            avstandSlakkM: slakkM,
            tidsdiffS,
            tidsslakkS,
            kursDeg: kurs,
            kursSlakkDeg: kursSlakk,
            fartMidtMs: fartMidt,
            fartMinMs: fartMin,
            fartMaksMs: fartMaks,
            sammeSensor,
            rekkefolgeSikker,
            urimeligFart,
            bruddIKjede,
            advarsler: beinAdv,
        });
        // Begrunnelseslinje per bein — dette er det operatøren etterprøver.
        if (sammeSensor) {
            begrunnelse.push(`${sa.sensorId} → ${sb.sensorId}: samme sensor, ${fmt(Math.abs(tidsdiffS))} s senere. Ingen fart kan regnes.`);
        }
        else {
            const fartTekst = fartMin === undefined
                ? 'fart kan ikke regnes'
                : fartMaks === undefined
                    ? `fart ≥ ${fmt(fartMin)} m/s (ingen øvre grense — klokkeslarken slår ut tidsdifferansen)`
                    : `fart ${fmt(fartMin)}–${fmt(fartMaks)} m/s (naiv ${fartMidt !== undefined ? fmt(fartMidt) : '–'} m/s)`;
            begrunnelse.push(`${sa.sensorId} → ${sb.sensorId}: ${Math.round(avstandM)} m ±${Math.round(slakkM)} m på ${fmt(Math.abs(tidsdiffS))} s ±${fmt(tidsslakkS)} s ⇒ ${fartTekst}${kurs !== undefined ? `, kurs ${Math.round(kurs)}°${kursSlakk !== undefined ? ` ±${Math.round(kursSlakk)}°` : ''}` : ''}.`);
        }
        for (const a2 of beinAdv)
            begrunnelse.push(`  ↳ ${a2}`);
    }
    // Vendinger: en kursendring over terskelen betyr at målet snudde — eller at
    // det er to mål. Vi teller den IKKE som et nytt mål (én person kan gå tilbake),
    // men den gjør tolkningen tvetydig, og det skal fram.
    let vendinger = 0;
    for (let i = 1; i < bein.length; i++) {
        const f = bein[i - 1].kursDeg;
        const t = bein[i].kursDeg;
        if (f === undefined || t === undefined)
            continue;
        if (vinkelDiffDeg(f, t) >= VENDING_TERSKEL_DEG) {
            vendinger++;
            begrunnelse.push(`retningsvending ved ${bein[i].fraSensorId}: kurs ${Math.round(f)}° → ${Math.round(t)}° (${Math.round(vinkelDiffDeg(f, t))}° endring). Samme mål som gikk tilbake, eller to ulike mål.`);
        }
    }
    const gjentakelser = bein.filter((b) => b.sammeSensor).length;
    const brudd = bein.filter((b) => b.bruddIKjede).length;
    const umulige = bein.filter((b) => b.urimeligFart).length;
    const usikkerRekkefolge = bein.filter((b) => !b.rekkefolgeSikker).length;
    // Antall passerende. Beste hypotese: hver kjede-brudd og hvert fysisk umulig
    // bein deler sekvensen i to. Nedre grense: ett mål, med mindre et bein er
    // fysisk umulig (da MÅ det være minst to). Øvre grense: ett mål per utløsning
    // — alltid mulig, fordi UGS ikke har målidentitet.
    const antattAntall = 1 + brudd + umulige;
    const minAntall = 1 + umulige;
    const maksAntall = rekke.length;
    const forste = kart.get(rekke[0].sensorId);
    const siste = kart.get(rekke[rekke.length - 1].sensorId);
    const nettoAvstandM = haversineM(sensorPos(forste), sensorPos(siste));
    const stiLengdeM = bein.reduce((s, b) => s + b.avstandM, 0);
    let nettoKursDeg;
    let nettoKursSlakkDeg;
    let nettoFartMinMs;
    let nettoFartMaksMs;
    let nettoFartMidtMs;
    if (forste.sensorId !== siste.sensorId && nettoAvstandM > 0) {
        const slakkM = dekningsradiusM(forste) + posisjonUsikkerhetM(forste) + dekningsradiusM(siste) + posisjonUsikkerhetM(siste);
        const tidsslakkS = (rekke[0].tidsusikkerhetMs + rekke[rekke.length - 1].tidsusikkerhetMs + 2 * ekstra) / 1000;
        nettoKursDeg = kursDegMellom(sensorPos(forste), sensorPos(siste));
        nettoKursSlakkDeg = nettoAvstandM <= slakkM ? 180 : (Math.atan(slakkM / nettoAvstandM) * 180) / Math.PI;
        const dtMaks = varighetS + tidsslakkS;
        const dtMin = varighetS - tidsslakkS;
        nettoFartMinMs = Math.max(0, nettoAvstandM - slakkM) / dtMaks;
        if (dtMin > 0)
            nettoFartMaksMs = (nettoAvstandM + slakkM) / dtMin;
        if (varighetS > 0)
            nettoFartMidtMs = nettoAvstandM / varighetS;
        begrunnelse.push(`NETTO: ${Math.round(nettoAvstandM)} m i luftlinje fra ${forste.sensorId} til ${siste.sensorId}, kurs ${Math.round(nettoKursDeg)}° ±${Math.round(nettoKursSlakkDeg)}°, netto fart ${fmt(nettoFartMinMs)}–${nettoFartMaksMs !== undefined ? fmt(nettoFartMaksMs) : '∞'} m/s. Stien er ${Math.round(stiLengdeM)} m — er den mye lenger enn luftlinja, har målet ikke gått rett fram (eller det er ikke ett mål).`);
    }
    else {
        begrunnelse.push(`NETTO: første og siste utløsning er på samme sensor (${forste.sensorId}) — ingen netto bevegelsesretning finnes.`);
    }
    // Tolkningsforbeholdet. Dette skal ALLTID stå, også når alt ser rent ut: det
    // er ikke en advarsel om dårlige data, det er en egenskap ved sensortypen.
    begrunnelse.push(`TOLKNING: rekkefølgen er KONSISTENT med ${antattAntall} mål i bevegelse, men ${maksAntall} uavhengige mål som utløser i samme rekkefølge gir presis samme datasett. En UGS gir tid og sted, aldri målidentitet.`);
    begrunnelse.push(`ANTALL PASSERENDE: antatt ${antattAntall} (mulig ${minAntall}–${maksAntall}).`);
    if (usikkerRekkefolge > 0) {
        advarsler.push(`${usikkerRekkefolge} av ${bein.length} bein har tidsdifferanse mindre enn klokkeslarken — for de beina er ikke rekkefølgen bestemt, og retningen kan være motsatt`);
    }
    if (umulige > 0) {
        advarsler.push(`${umulige} bein har fysisk umulig fart (> ${maksFart} m/s selv i nedre grense) — minst to ulike mål, eller klokkefeil`);
    }
    if (brudd > 0)
        advarsler.push(`${brudd} brudd i kjeden (opphold over ${maksOpphold / 1000} s) — behandle sidene som ulike hendelser`);
    if (gjentakelser > 0)
        advarsler.push(`${gjentakelser} gjentatt(e) utløsning(er) på samme sensor — «fram og tilbake» kan ikke skilles fra «to mål» på én sensor`);
    if (vendinger > 0)
        advarsler.push(`${vendinger} retningsvending(er) over ${VENDING_TERSKEL_DEG}° — sekvensen kan være to ulike bevegelser`);
    if (nettoFartMidtMs !== undefined && nettoFartMidtMs > 0 && nettoFartMidtMs < TYPISK_GANGFART_MS / 4) {
        advarsler.push(`netto fart ${fmt(nettoFartMidtMs)} m/s er langt under gangfart (${TYPISK_GANGFART_MS} m/s) — enten stoppet målet lenge, eller utløsningene tilhører ulike mål/hendelser`);
    }
    if (stiLengdeM > 0 && nettoAvstandM > 0 && stiLengdeM > 2.5 * nettoAvstandM) {
        advarsler.push(`stien (${Math.round(stiLengdeM)} m) er over 2,5× luftlinja (${Math.round(nettoAvstandM)} m) — mønsteret ser ut som søk/patruljering, eller som flere mål som er tolket som ett`);
    }
    const tvetydig = brudd > 0 || umulige > 0 || gjentakelser > 0 || vendinger > 0 || usikkerRekkefolge > 0;
    return {
        ok: true,
        sekvens: {
            antallUtlosninger: rekke.length,
            antallSensorer: sensorIder.size,
            varighetS,
            bein,
            stiLengdeM,
            nettoAvstandM,
            nettoKursDeg,
            nettoKursSlakkDeg,
            nettoFartMinMs,
            nettoFartMaksMs,
            nettoFartMidtMs,
            antallPasserendeAntatt: antattAntall,
            antallPasserendeMin: minAntall,
            antallPasserendeMaks: maksAntall,
            tvetydig,
            begrunnelse,
            advarsler,
        },
    };
}
/**
 * DEKNINGSHULL — hvilke deler av en rute/akse er IKKE dekket av noen sensor.
 *
 * Dette er analysen et jegerlag trenger FØR de legger ut, og etterpå for å svare
 * på det ene spørsmålet som betyr noe: HVOR KAN NOEN GÅ FORBI UOPPDAGET? Den er
 * bevisst konservativ på tre punkter:
 *
 *  1. Bare AKTIVE sensorer teller (med mindre `taMedInaktive`). En hentet eller
 *     utladet sensor som fortsatt tegnes som dekning er verre enn ingen sensor,
 *     fordi laget da tror aksen er dekket.
 *  2. Sektorer dekker bare sektoren sin, og brytertråder dekker bare metrene ved
 *     tråden. Å regne dem som disker ville lukket hull som er åpne.
 *  3. TERRENG ER IKKE MODELLERT. Radien er operatørens vurdering av rekkevidde i
 *     dette terrenget; en rygg eller en myr mellom sensor og rute kan gjøre den
 *     mye kortere. Analysen sier derfor «ikke dekket» med sikkerhet, men
 *     «dekket» bare med det forbeholdet — og det står i `advarsler`.
 *
 * Metoden er diskretisering: ruta deles i N celler av lik lengde, og cellens
 * MIDTPUNKT testes mot hver sensor. Hull er sammenhengende løp av udekkede
 * celler. Kvantiseringen er ±`opplosningM`, og den rapporteres i stedet for å
 * skjules — et hull på «398 m» fra en 5 m-oppløsning er 400 m ±5 m.
 */
export function dekningshull(sensorer, rute, valg = {}) {
    if (!Array.isArray(sensorer) || !Array.isArray(rute))
        return { ok: false, arsak: 'ugyldige inndata' };
    if (rute.length < 2)
        return { ok: false, arsak: 'ruta må ha minst 2 punkt — ett punkt er ikke en akse å gå langs' };
    if (rute.length > MAX_RUTEPUNKT)
        return { ok: false, arsak: `for mange rutepunkt (maks ${MAX_RUTEPUNKT})` };
    if (sensorer.length > MAX_SENSORER)
        return { ok: false, arsak: `for mange sensorer (maks ${MAX_SENSORER})` };
    for (const p of rute) {
        if (!Number.isFinite(p.lat) || !Number.isFinite(p.lon) || Math.abs(p.lat) > 90 || Math.abs(p.lon) > 180) {
            return { ok: false, arsak: 'rutepunkt utenfor gyldig lat/lon' };
        }
    }
    const advarsler = [];
    const begrunnelse = [];
    // Rutens bein, med kumulativ lengde. Kursen regnes med `kursDegMellom` og
    // punktene interpoleres med `project` — samme jordmodell som avstandene.
    const beinLengde = [];
    const beinKurs = [];
    let ruteLengdeM = 0;
    for (let i = 1; i < rute.length; i++) {
        const d = haversineM(rute[i - 1], rute[i]);
        beinLengde.push(d);
        beinKurs.push(kursDegMellom(rute[i - 1], rute[i]));
        ruteLengdeM += d;
    }
    if (ruteLengdeM <= 0)
        return { ok: false, arsak: 'ruta har null lengde (alle punkt er like)' };
    const punktLangs = (s) => {
        let rest = Math.max(0, Math.min(ruteLengdeM, s));
        for (let i = 0; i < beinLengde.length; i++) {
            const L = beinLengde[i];
            if (rest <= L || i === beinLengde.length - 1) {
                return project(rute[i], beinKurs[i], Math.min(rest, L));
            }
            rest -= L;
        }
        return rute[rute.length - 1];
    };
    // Sensorutvalg. Hver utelatelse får en ÅRSAK — en sensor som stilltiende
    // forsvinner fra analysen er en analyse ingen kan etterprøve.
    const vurderte = [];
    const utelatte = [];
    for (const s of sensorer) {
        if (!s.aktiv && !valg.taMedInaktive) {
            utelatte.push({ sensorId: s.sensorId, arsak: s.fjernetMs !== undefined ? 'hentet inn (fjernetMs satt)' : 'ikke aktiv' });
            continue;
        }
        if (valg.krevSambandslenke && (s.lenke.kvalitet === 'tapt' || s.lenke.kvalitet === 'ingen-lenke')) {
            utelatte.push({ sensorId: s.sensorId, arsak: `lenke «${s.lenke.kvalitet}» — gir ingen varsel i tid` });
            continue;
        }
        vurderte.push(s);
    }
    const harLinje = vurderte.some((s) => s.geometri.form === 'linje');
    // En brytertråd dekker bare ~BRYTERTRAD_DEKNING_M på tvers. Med grovere celler
    // enn det ville midtpunkttesten hoppet rett over tråden og rapportert hull der
    // det er en sperre. Derfor tvinges oppløsningen ned når en tråd er med.
    let onsketSteg = Math.max(0.25, valg.stegM ?? 5);
    if (harLinje)
        onsketSteg = Math.min(onsketSteg, BRYTERTRAD_DEKNING_M);
    // −1e-9 er en flyttallsgarderobe, ikke slurv: en rute som er 1000 m «på papiret»
    // kommer ut av haversineM som 1000,0000000002, og uten guarden gir ceil() da 201
    // celler i stedet for 200. Cellelengden blir da 4,9751 m, og hullkantene lander
    // på 398,01 i stedet for 400 — tall operatøren ikke kan etterregne.
    let antallCeller = Math.max(1, Math.ceil(ruteLengdeM / onsketSteg - 1e-9));
    if (antallCeller > MAX_CELLER) {
        antallCeller = MAX_CELLER;
        advarsler.push(`ruta er ${Math.round(ruteLengdeM)} m; oppløsningen er grovet ned til ${(ruteLengdeM / antallCeller).toFixed(1)} m for å holde ${MAX_CELLER} celler — små hull (og brytertråder) kan bli oversett`);
    }
    const h = ruteLengdeM / antallCeller;
    const dekket = new Array(antallCeller).fill(false);
    const sperreSett = new Map();
    for (let i = 0; i < antallCeller; i++) {
        const s = (i + 0.5) * h;
        const p = punktLangs(s);
        for (const sensor of vurderte) {
            if (!dekkerPunktUgs(sensor, p))
                continue;
            dekket[i] = true;
            if (sensor.geometri.form === 'linje' && !sperreSett.has(sensor.sensorId)) {
                sperreSett.set(sensor.sensorId, { sensorId: sensor.sensorId, langsM: s, punkt: p });
            }
        }
    }
    let dekketM = 0;
    for (const d of dekket)
        if (d)
            dekketM += h;
    const hull = [];
    let start = null;
    for (let i = 0; i <= antallCeller; i++) {
        const erUdekket = i < antallCeller && !dekket[i];
        if (erUdekket && start === null)
            start = i;
        if (!erUdekket && start !== null) {
            const fraM = start * h;
            const tilM = i * h;
            hull.push({
                fraM,
                tilM,
                lengdeM: tilM - fraM,
                fraPunkt: punktLangs(fraM),
                tilPunkt: punktLangs(tilM),
                midtPunkt: punktLangs((fraM + tilM) / 2),
            });
            start = null;
        }
    }
    const storsteHullM = hull.reduce((m, x) => Math.max(m, x.lengdeM), 0);
    const sperrepunkter = [...sperreSett.values()].sort((a, b) => a.langsM - b.langsM);
    begrunnelse.push(`Rute ${Math.round(ruteLengdeM)} m i ${rute.length} punkt, analysert i ${antallCeller} celler à ${h.toFixed(2)} m.`);
    begrunnelse.push(`${vurderte.length} sensor(er) vurdert${utelatte.length > 0 ? `, ${utelatte.length} utelatt (${utelatte.map((u) => `${u.sensorId}: ${u.arsak}`).join('; ')})` : ''}.`);
    begrunnelse.push(`Dekket ${Math.round(dekketM)} m av ${Math.round(ruteLengdeM)} m (${Math.round((dekketM / ruteLengdeM) * 100)} %). ${hull.length} hull, største ${Math.round(storsteHullM)} m.`);
    for (const x of hull) {
        begrunnelse.push(`  HULL ${Math.round(x.fraM)}–${Math.round(x.tilM)} m (${Math.round(x.lengdeM)} m): ingen sensor dekker her. Midt i hullet: ${x.midtPunkt.lat.toFixed(5)}, ${x.midtPunkt.lon.toFixed(5)}.`);
    }
    for (const sp of sperrepunkter) {
        begrunnelse.push(`  SPERRE ved ${Math.round(sp.langsM)} m: brytertråd ${sp.sensorId} krysser ruta. Den som FØLGER ruta kan ikke passere uoppdaget, men tråden lukker bare ~${BRYTERTRAD_DEKNING_M} m — hullene på hver side er reelle for den som går utenom.`);
    }
    if (vurderte.length === 0) {
        advarsler.push('ingen sensorer i analysen — hele ruta er ett hull');
    }
    advarsler.push('TERRENG ER IKKE MODELLERT: «ikke dekket» er sikkert, men «dekket» forutsetter at deteksjonsradien holder i dette terrenget. Rygger, myr og tett gran forkorter den kraftig');
    const anslatte = vurderte.filter((s) => s.posisjonMetode === 'anslått');
    if (anslatte.length > 0) {
        advarsler.push(`${anslatte.length} sensor(er) har ANSLÅTT posisjon (${anslatte.map((s) => s.sensorId).join(', ')}) — hullkantene kan ligge titalls meter feil`);
    }
    const marginale = vurderte.filter((s) => s.lenke.kvalitet === 'marginal' || s.lenke.kvalitet === 'tapt');
    if (marginale.length > 0 && !valg.krevSambandslenke) {
        advarsler.push(`${marginale.length} sensor(er) har svak/tapt lenke (${marginale.map((s) => s.sensorId).join(', ')}) og teller likevel som dekning — sett krevSambandslenke: true for å se hvor VARSLING mangler`);
    }
    if (hull.length === 0) {
        begrunnelse.push('Ingen hull funnet i denne oppløsningen — men se forbeholdet om terreng.');
    }
    return {
        ok: true,
        analyse: {
            ruteLengdeM,
            opplosningM: h,
            dekketM,
            dekningsandel: dekketM / ruteLengdeM,
            hull,
            storsteHullM,
            sperrepunkter,
            vurderteSensorer: vurderte.map((s) => s.sensorId),
            utelatteSensorer: utelatte,
            begrunnelse,
            advarsler,
        },
    };
}
//# sourceMappingURL=ugs.js.map